import BlockContent from '@sanity/block-content-to-react'
import imageUrlBuilder from '@sanity/image-url'
import dayjs from 'dayjs'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useNextSanityImage } from 'next-sanity-image'
import { useId } from 'react'
import Sidebar from '@/components/learn/Sidebar'
import LearnBreadcrumbs from '@/components/learn/LearnBreadcrumbs'
import MetaTags from '@/components/MetaTags'
import { client } from '@/sanity/lib/client'
import { generateArticleSchema } from '@/utils/ArticleJsonLd'
import { sanityDownloadUrl } from '@/lib/sanity-download-url'
import BookingLink from '@/components/ui/booking-link'
import { dataset, projectId } from '@/sanity/env'

const imageConfig = { dataset, projectId }

// shortcut: Sanity retains these old URLs; update the stored links when editing these articles.
const updatedLinks = {
  'https://www.venturawebdesign.com/social-media/': '/social-media-marketing',
  'https://www.venturawebdesign.com/local-listing-management': '/seo',
  'https://www.venturawebdesign.com/addons/custom-404-pages': '/vip-support',
  'https://www.threekit.com/discovery-': 'https://www.threekit.com/',
  'https://mixpanel.com/blog/optimizing-above-the-fold/': 'https://www.nngroup.com/articles/scrolling-and-attention/',
  'https://secure.bingads.microsoft.com/': 'https://about.ads.microsoft.com/en/sign-in',
}

function extractYouTubeId(url) {
  const regex =
    /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/\s]{11})/i
  const match = url.match(regex)
  return match ? match[1] : null
}

export default function ResourcePostPage({
  post,
  categories,
  categoriesWithPostCounts,
}) {
  const router = useRouter()
  const articleContentId = useId()
  const imageProps = useNextSanityImage(imageConfig, post?.mainImage)

  if (router.isFallback) {
    return <div>Loading...</div>
  }

  const articleSchema = generateArticleSchema(post)

  const builder = imageUrlBuilder(imageConfig)
  function urlFor(source) {
    return builder.image(source)
  }

  const serializers = {
    marks: {
      link: (props) => {
        const { mark, children } = props
        const href = updatedLinks[mark?.href?.split('#')[0]] || mark?.href

        if (!href) {
          return <span>{children}</span>
        }

        if (href.startsWith('https://calendly.com/')) {
          return <BookingLink href={href}>{children}</BookingLink>
        }

        if (href.includes('youtube.com') || href.includes('youtu.be')) {
          const videoId = extractYouTubeId(href)
          if (videoId) {
            return (
              <div className="my-6 w-full">
                <iframe
                  title="Youtube Video"
                  className="h-auto w-full"
                  style={{ aspectRatio: '16/9' }}
                  src={`https://www.youtube.com/embed/${videoId}`}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )
          }
        }

        return (
          <a
            href={href}
            target={mark.blank ? '_blank' : undefined}
            rel={mark.blank ? 'noopener noreferrer' : undefined}
          >
            {children}
            {mark.blank && (
              <span className="sr-only"> (opens in a new tab)</span>
            )}
          </a>
        )
      },
    },
    types: {
      block: (props) => {
        const { node, children } = props
        const style = node.style || 'normal'
        const headingProps = { id: node.anchor, className: 'scroll-mt-28' }

        if (node.markDefs?.some(mark => mark._type === 'link' && extractYouTubeId(mark.href || ''))) {
          return <div className="my-4">{children}</div>
        }

        switch (style) {
          case 'h1':
            return <h1 {...headingProps}>{children}</h1>
          case 'h2':
            return <h2 {...headingProps}>{children}</h2>
          case 'h3':
            return <h3 {...headingProps}>{children}</h3>
          case 'h4':
            return <h4 {...headingProps}>{children}</h4>
          case 'blockquote':
            return <blockquote>{children}</blockquote>
          default:
            return (
              <>
                <p>{children}</p>
                {/* shortcut: This guide supplement is stored here; move it into Sanity when editing the article there. */}
                {post.slug.current ===
                  'guide-to-building-a-new-website-or-redesigning-an-existing-website' &&
                  node._key === '1ae3a3fb1810' && (
                    <p>
                      If your online store has problems, you may not know what to
                      fix. An{' '}
                      <Link href="/ecommerce-website-audit">
                        ecommerce website audit
                      </Link>{' '}
                      can help. Our team checks your store and the tools you use
                      to run it. We show you what to fix first and whether you
                      need a new design.
                    </p>
                  )}
              </>
            )
        }
      },
      table: ({ node }) => (
        <div
          role="region"
          aria-label={node.title}
          tabIndex={0}
          className="my-8 overflow-x-auto rounded-lg border border-blue-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-700"
        >
          <table className="!my-0 !table min-w-[630px] text-base">
            <thead className="bg-blue-800">
              <tr>
                {node.headers.map((header, index) => (
                  <th key={index} scope="col" className="!p-4 !text-white">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {node.rows.map((row) => (
                <tr key={row._key} className="odd:bg-blue-50">
                  {row.cells.map((cell, index) => (
                    <td key={index} className="!p-4 align-top">
                      {cell}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ),
      file: ({ node }) => {
        if (!node.url) return null
        const href = sanityDownloadUrl(node.url, node.filename)
        return (
          <section
            aria-label={node.title}
            id={node.anchor}
            className="not-prose my-10 scroll-mt-28 rounded-xl bg-blue-50 p-6 text-center sm:p-8"
          >
            {node.image?.asset && (
              <a href={href} aria-label={`Download ${node.title}`}>
                <Image
                  src={urlFor(node.image).width(328).url()}
                  alt={node.image.alt || node.title}
                  width={164}
                  height={212}
                  sizes="164px"
                  className="!mx-auto !mb-6 !mt-0 !h-auto !w-40 rounded-lg shadow-sm"
                />
              </a>
            )}
            <h2 className="mb-4 text-2xl font-bold leading-tight text-blue-950">
              {node.title}
            </h2>
            <p className="mb-6 text-base leading-relaxed text-blue-950">
              {node.description}
            </p>
            <a
              href={href}
              className="inline-block rounded-lg bg-blue-800 px-6 py-3 text-base font-semibold text-white hover:bg-blue-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-800"
            >
              Download the free PDF
            </a>
          </section>
        )
      },
      image: ({ node }) => {
        if (!node || !node.asset) {
          return null
        }

        const imageUrl = urlFor(node).width(800).url()

        return (
          <Image
            src={imageUrl}
            alt={node.alt || 'Blog post image'}
            width={800}
            height={450}
            className="my-6 h-auto w-full rounded-lg"
            sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
          />
        )
      },
    },
  }

  return (
    <>
      <MetaTags
        pageTitle={post.title}
        pageDescription={post.excerpt}
        path={`/resources/${post.slug.current}`}
        image={imageProps?.src}
        type="article"
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(articleSchema),
        }}
      />

      <LearnBreadcrumbs
        categoryTitle={post.categories[0]?.title}
        categorySlug={post.categories[0]?.slug.current}
        postTitle={post.title}
      />

      <div className="wrapper mb-20 grid grid-cols-1 gap-8 lg:grid-cols-12">
        <article className="prose prose-lg mx-auto w-full max-w-none !text-black marker:text-black lg:col-span-8">
          <h1 className="mb-2.5 text-3xl font-extrabold leading-tight sm:text-4xl lg:mb-5 lg:text-5xl">
            {post.title}
          </h1>

          <div className="mr-auto flex flex-wrap items-center gap-2.5 lg:flex-row-reverse lg:justify-end lg:gap-1">
            <div className="text-sm text-neutral-500">
              <p className="my-0 mr-2">
                {post.lastModified && post.lastModified !== post.publishedAt ? (
                  <>
                    Last updated on{' '}
                    {dayjs(post.lastModified).format('MMMM D, YYYY')} by{' '}
                    <Link href="/about/kevin" className="font-normal">
                      {post.author}
                    </Link>
                  </>
                ) : (
                  <>
                    Posted on {dayjs(post.publishedAt).format('MMMM D, YYYY')}{' '}
                    by{' '}
                    <Link href="/about/kevin" className="font-normal">
                      {post.author}
                    </Link>
                  </>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              {post.categories.map((category) => (
                <Link
                  key={category._id}
                  href={`/resources/category/${category.slug.current}`}
                  className="rounded-full bg-gray-100 px-2 py-1 text-xs font-normal leading-none text-neutral-600 hover:!underline"
                >
                  {category.title}
                </Link>
              ))}
            </div>
          </div>
          {imageProps && (
            <Image
              {...imageProps}
              className="h-auto w-full rounded-lg object-cover"
              priority
              alt={post.mainImage.alt || `Cover for ${post.title}`}
              sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
            />
          )}
          <div id={articleContentId} className="prose-content overflow-hidden">
            <BlockContent blocks={post.body} serializers={serializers} />
          </div>
        </article>

        <div className="lg:col-span-4">
          <Sidebar
            categories={categories}
            categoriesWithPostCounts={categoriesWithPostCounts}
          />
        </div>
      </div>
    </>
  )
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  const { slug } = params
  const post = await client.fetch(
    `*[_type == "post" && slug.current == $slug][0] {
      _id,
      title,
      publishedAt,
      lastModified,
      slug,
      mainImage,
      categories[]->{ _id, title, slug },
      body[]{
        ...,
        _type == "file" => {
          "url": asset->url,
          "filename": asset->originalFilename
        }
      },
      excerpt,
      "author": author->name
    }`,
    { slug },
  )

  if (!post) {
    return { notFound: true }
  }

  const categories = await client.fetch(
    `*[_type == "category"]{
      _id,
      title,
      slug
    }`,
  )

  const categoriesWithPostCounts = await Promise.all(
    categories.map(async (category) => {
      const postCountQuery = `count(*[_type == "post" && references($categoryId)])`
      const categoryPostCount = await client.fetch(postCountQuery, {
        categoryId: category._id,
      })
      return {
        ...category,
        postCount: categoryPostCount,
      }
    }),
  )

  return {
    props: {
      post,
      categories,
      categoriesWithPostCounts,
    },
    revalidate: 60,
  }
}
